import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, Box, Tooltip } from '@mui/material';

// project-imports
import { dispatch, useSelector } from 'store';
import { activeItem, openDrawer } from 'store/reducers/menu';
import SIDEBAR from '../../sidebarStyles';

// ==============================|| NAVIGATION - ITEM ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { drawerOpen, openItem } = useSelector((state) => state.menu);

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = { component: forwardRef((props, ref) => <Link {...props} to={item.url} target={itemTarget} ref={ref} />) };
  if (item?.external) {
    // external links are opened in a new tab, so pair the target with rel to prevent
    // the opened page from reaching back through window.opener (reverse tabnabbing)
    listItemProps = { component: 'a', href: item.url, target: itemTarget, rel: 'noopener noreferrer' };
  }
  const isSelected = openItem.findIndex((id) => id === item.id) > -1;

  const Icon = item.icon;
  // solid icon for the active route, light outline otherwise — reinforces hierarchy at a glance
  const itemIcon = item.icon ? <Icon variant={isSelected ? 'Bulk' : 'Linear'} size={drawerOpen ? 19 : 22} /> : false;

  const { pathname } = useLocation();

  // active menu item on page load
  useEffect(() => {
    if (pathname === item.url) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [pathname]);

  const navButton = (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      selected={isSelected}
      aria-current={isSelected ? 'page' : undefined}
      sx={{
        position: 'relative',
        zIndex: 1201,
        pl: drawerOpen ? `${level * 18 + 14}px` : 1.5,
        pr: 1.5,
        py: drawerOpen ? 0.9 : level === 1 ? 1.25 : 1,
        minHeight: drawerOpen ? 40 : 'auto',
        mx: drawerOpen ? 1.25 : 'auto',
        my: 0.2,
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'background-color 0.18s ease, color 0.18s ease',
        backgroundColor: isSelected ? SIDEBAR.activeBg : 'transparent',
        '&::before': isSelected
          ? {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              height: '100%',
              width: '3px',
              borderRadius: '3px 0 0 3px',
              backgroundColor: SIDEBAR.accent
            }
          : {},
        '&:hover': {
          backgroundColor: isSelected ? SIDEBAR.activeBgHover : SIDEBAR.hoverBg
        },
        // keyboard users previously got no visible affordance at all
        '&.Mui-focusVisible': {
          backgroundColor: isSelected ? SIDEBAR.activeBgHover : SIDEBAR.hoverBg,
          outline: `2px solid ${SIDEBAR.focusRing}`,
          outlineOffset: '-2px'
        },
        '&.Mui-selected': {
          backgroundColor: SIDEBAR.activeBg,
          '&:hover': {
            backgroundColor: SIDEBAR.activeBgHover
          }
        },
        '&.Mui-disabled': {
          opacity: 0.38
        },
        ...(!drawerOpen &&
          level === 1 && {
            mx: 'auto',
            width: 46,
            height: 46,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px'
          })
      }}
      {...(downLG && {
        onClick: () => dispatch(openDrawer(false))
      })}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: drawerOpen ? 34 : 'unset',
            color: isSelected ? SIDEBAR.accent : SIDEBAR.icon,
            transition: 'color 0.18s ease',
            ...(!drawerOpen &&
              level === 1 && {
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}

      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                color: isSelected ? SIDEBAR.textActive : SIDEBAR.text,
                fontWeight: isSelected ? 600 : 400,
                fontSize: '0.875rem',
                letterSpacing: '0.01em',
                transition: 'color 0.18s ease',
                lineHeight: 1.45,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {item.title}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          sx={{ ml: 0.5, height: 20, fontSize: '0.6875rem' }}
        />
      )}
    </ListItemButton>
  );

  // In collapsed mode show tooltip with item title on hover
  if (!drawerOpen && level === 1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 0.5 }}>
        <Tooltip title={item.title} placement="right" arrow>
          {navButton}
        </Tooltip>
      </Box>
    );
  }

  return navButton;
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
  target: PropTypes.object,
  url: PropTypes.string,
  id: PropTypes.object,
  disabled: PropTypes.bool,
  chip: PropTypes.object,
  variant: PropTypes.string,
  title: PropTypes.string,
  avatar: PropTypes.object,
  label: PropTypes.string
};

export default NavItem;
